import { useCallback, useEffect, useMemo, useRef } from "react";
import {
  getCorpusFeed,
  updateCorpusFeed,
  getCorpusFeedComposition,
  listCorpusFeedRuns,
  type BatchJob,
} from "../../../../lib/corpusApi";
import { useAsyncData, type AsyncDataResult } from "../../../../hooks/useAsyncData";
import {
  useAsyncPaginatedData,
  type UsePaginatedDataResult,
} from "../../../../hooks/useAsyncPaginatedData";
import { useFeedStatistics, type UseFeedStatisticsResult } from "../Feeds/useFeedStatistics";
import type {
  CorpusFeed,
  CorpusFeedComposition,
  UpdateCorpusFeedParams,
} from "@shared/types/corpus/api.ts";

const RUNS_PAGE_SIZE = 10;

export interface UseFeedDetailResult {
  feed: AsyncDataResult<CorpusFeed>;
  /** The feed's statistics, once they have loaded, apart from the feed. */
  statistics: UseFeedStatisticsResult;
  /** What the feed's papers are made of, loaded apart from the feed. */
  composition: AsyncDataResult<CorpusFeedComposition>;
  runs: UsePaginatedDataResult<BatchJob>;
  /** Loads everything about the feed again, for after something has changed it. */
  reloadAll: () => Promise<void>;
  /** Saves a new name or description, then loads the feed again. Throws, so the field can say why. */
  saveDetails: (changes: UpdateCorpusFeedParams) => Promise<void>;
}

/**
 * One corpus feed and what goes with it: its statistics, its composition and its runs. Each loads
 * on its own, so the feed shows before the numbers do. When the feed's open run finishes, all of
 * them load again, so the page shows its result without a reload.
 */
export function useFeedDetail(feedId: string): UseFeedDetailResult {
  const feed = useAsyncData(() => getCorpusFeed(feedId));
  const composition = useAsyncData(() => getCorpusFeedComposition(feedId));
  const runs = useAsyncPaginatedData((page) => listCorpusFeedRuns(feedId, page), {
    pageSize: RUNS_PAGE_SIZE,
  });
  const feeds = useMemo(() => (feed.data ? [feed.data] : []), [feed.data]);
  const statistics = useFeedStatistics(feeds);

  const { refresh: refreshFeed } = feed;
  const { refresh: refreshComposition } = composition;
  const { refresh: refreshRuns } = runs;
  const { reload: reloadStatistics } = statistics;
  const reloadAll = useCallback(async () => {
    await Promise.all([
      refreshFeed(),
      refreshComposition(),
      refreshRuns(),
      reloadStatistics([feedId]),
    ]);
  }, [feedId, refreshFeed, refreshComposition, refreshRuns, reloadStatistics]);

  const saveDetails = useCallback(
    async (changes: UpdateCorpusFeedParams) => {
      await updateCorpusFeed(feedId, changes);
      await reloadAll();
    },
    [feedId, reloadAll]
  );

  const runIsOpen = statistics.byFeed[feedId]?.openRun != null;
  const wasOpen = useRef(false);
  useEffect(() => {
    if (wasOpen.current && !runIsOpen) void reloadAll();
    wasOpen.current = runIsOpen;
  }, [runIsOpen, reloadAll]);

  return { feed, statistics, composition, runs, reloadAll, saveDetails };
}
